import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import type { Work } from "@/mocks/works";
import { workFormat } from "@/mocks";

interface Props {
  slides: Work[];
}

export default function HeroCarousel({ slides }: Props) {
  const [active, setActive] = useState(0);
  const [paused, setPaused] = useState(false);
  const touchStart = useRef<number | null>(null);
  const timer = useRef<number | null>(null);

  useEffect(() => {
    if (paused || slides.length <= 1) return;
    timer.current = window.setTimeout(() => {
      setActive((prev) => (prev + 1) % slides.length);
    }, 6000);
    return () => {
      if (timer.current) window.clearTimeout(timer.current);
    };
  }, [active, paused, slides.length]);

  const go = (dir: number) => setActive((prev) => (prev + dir + slides.length) % slides.length);

  if (!slides.length) return null;

  return (
    <section className="w-full bg-background-50 pt-4 md:pt-6">
      <div className="mx-auto w-full max-w-content px-4 md:px-6">
        <div
          className="relative w-full overflow-hidden rounded-lg bg-primary-900"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onTouchStart={(e) => {
            touchStart.current = e.touches[0].clientX;
          }}
          onTouchEnd={(e) => {
            if (touchStart.current === null) return;
            const delta = e.changedTouches[0].clientX - touchStart.current;
            if (Math.abs(delta) > 48) go(delta > 0 ? -1 : 1);
            touchStart.current = null;
          }}
        >
          <div className="relative h-[440px] w-full md:h-[520px]">
            {slides.map((slide, i) => (
              <div
                key={slide.id}
                className={`absolute inset-0 transition-opacity duration-700 ${
                  i === active ? "opacity-100" : "pointer-events-none opacity-0"
                }`}
              >
                <img
                  src={slide.wide}
                  alt={`${slide.title} 剧照`}
                  title={`${slide.title} 剧情介绍`}
                  className="h-full w-full object-cover object-top"
                />
                <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/92 via-foreground-950/70 to-foreground-950/20" />
                <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-transparent to-foreground-950/20" />
              </div>
            ))}

            <div className="relative z-10 flex h-full w-full items-end md:items-center">
              <div className="w-full px-5 pb-8 md:px-12 md:pb-0">
                <div className="max-w-[560px]">
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-background-100/90">
                    <span className="rounded bg-accent-500 px-2 py-0.5 font-semibold text-background-50">重点推荐</span>
                    <span className="whitespace-nowrap">{workFormat(slides[active].type)}</span>
                    <span className="text-background-300">|</span>
                    <span className="whitespace-nowrap">{slides[active].year}</span>
                    <span className="text-background-300">|</span>
                    <span className="whitespace-nowrap">{slides[active].region}</span>
                  </div>
                  <h2
                    key={slides[active].id}
                    className="animate-fade-up mt-4 font-heading text-2xl font-bold leading-tight text-background-50 md:text-[38px]"
                  >
                    {slides[active].title}
                  </h2>
                  <p className="animate-fade-up delay-1 mt-3 line-clamp-2 max-w-[520px] text-sm leading-relaxed text-background-100/85 md:text-[15px]">
                    {slides[active].summary}
                  </p>
                  <div className="animate-fade-up delay-2 mt-4 flex flex-wrap items-center gap-3 text-xs text-background-100/80">
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-user-star-line text-sm leading-none text-accent-400" />
                      导演 {slides[active].director}
                    </span>
                    <span className="flex items-center gap-1 whitespace-nowrap">
                      <i className="ri-star-fill text-sm leading-none text-accent-400" />
                      {slides[active].rating.toFixed(1)} 分
                    </span>
                    <span className="hidden whitespace-nowrap sm:inline">{slides[active].status}</span>
                  </div>
                  <div className="animate-fade-up delay-3 mt-6 flex flex-wrap items-center gap-3">
                    <Link
                      to={`/detail/${slides[active].id}`}
                      className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-accent-600"
                    >
                      <i className="ri-play-circle-line text-base leading-none" />
                      查看剧情详情
                    </Link>
                    <Link
                      to="/category"
                      className="inline-flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:border-background-50 hover:bg-background-50/10"
                    >
                      浏览全部影视
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="absolute bottom-5 right-5 z-10 hidden items-center gap-3 md:flex">
              <button
                type="button"
                aria-label="上一张"
                onClick={() => go(-1)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/35 text-background-50 transition-colors hover:bg-background-50 hover:text-primary-800"
              >
                <i className="ri-arrow-left-s-line text-lg leading-none" />
              </button>
              <div className="flex items-center gap-1.5">
                {slides.map((slide, i) => (
                  <button
                    key={slide.id}
                    type="button"
                    aria-label={`切换到第 ${i + 1} 张推荐`}
                    onClick={() => setActive(i)}
                    className={`h-1.5 cursor-pointer rounded-full transition-all ${
                      i === active ? "w-7 bg-accent-500" : "w-3 bg-background-50/45 hover:bg-background-50/80"
                    }`}
                  />
                ))}
              </div>
              <button
                type="button"
                aria-label="下一张"
                onClick={() => go(1)}
                className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-background-50/35 text-background-50 transition-colors hover:bg-background-50 hover:text-primary-800"
              >
                <i className="ri-arrow-right-s-line text-lg leading-none" />
              </button>
            </div>

            <div className="absolute bottom-5 right-5 z-10 flex items-center gap-1.5 md:hidden">
              {slides.map((slide, i) => (
                <button
                  key={slide.id}
                  type="button"
                  aria-label={`切换到第 ${i + 1} 张推荐`}
                  onClick={() => setActive(i)}
                  className={`h-1.5 cursor-pointer rounded-full transition-all ${
                    i === active ? "w-6 bg-accent-500" : "w-1.5 bg-background-50/45"
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}